import { IconButton } from "@mui/material";
import { FC, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import {
  FilterActionsDividerStyled,
  FilterActionsStyled,
  FilterDropdownWrapper,
  FilterSelectControl,
  FramerTooltip,
  selectStyles,
  selectStylesMobile,
} from "@ryde/controls";
import { PageContext, PageHeaderContext, useReportId } from "@ryde/redux";
import {
  getGridLabelKey,
  IDropdownItem,
  IReportTemplateFilters,
  reportFiltersWithValuesAF,
  useIsMobile,
} from "@ryde/shared";

import { FilterClearButtonReports } from "../../../reports/filters/parts/filter-clear-button.reports";
import { FilterMultiSelectReports } from "../../../reports/filters/parts/filter-multiselect.reports";
import { FiltersByIdsReport } from "../../../reports/filters/parts/filters-by-ids.report";
import { useTableFiltersActionsReports } from "../../../reports/filters/parts/use-table-filters-actions-reports";
import { dashboardCardsAtom } from "../dynamic-dashboard.atoms";
import { REPORT_FILTER_ENTITY } from "../dynamic-dashboard.constants";
import {
  getHonourableKeys,
  pickOfferableFilters,
} from "../dynamic-dashboard.filters.utils";
import { useDashboardFilterSchema } from "../hooks/use-dashboard-filter-schema";
import { useIsDashboardLayoutResolved } from "../hooks/use-is-dashboard-layout-resolved";
import { usePruneUnhonourableFilters } from "../hooks/use-prune-unhonourable-filters";

/**
 * Add / clear / apply cluster. Reuses the reports' clear button (which only
 * touches the shared filter atom + PageHeaderContext); the add button offers only
 * the filters a card on this dashboard can actually honour, and apply just fires
 * PageHeaderContext's `onFiltersApply` (the page snapshots the live filters into
 * the applied atom).
 */
const DashboardFilterActions: FC<{ schema: IReportTemplateFilters }> = ({
  schema,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { reportId } = useReportId();
  const activeFilters = useRecoilValue(reportFiltersWithValuesAF(reportId));
  const { handleAddFilter } = useTableFiltersActionsReports();
  const { onFiltersApply } = useContext(PageHeaderContext);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const addableOptions = useMemo(
    () =>
      Object.keys(schema)
        .filter(key => !(key in (activeFilters ?? {})))
        .map(key => ({
          value: key,
          label: t(getGridLabelKey(REPORT_FILTER_ENTITY, key)),
        })) as IDropdownItem[],
    [schema, activeFilters, t],
  );

  const stylesComputed = useMemo(
    () => (isMobile ? selectStylesMobile : selectStyles),
    [isMobile],
  );

  const handleToggleAdd = useEvent(() => setIsAddOpen(prev => !prev));
  const handleCloseAdd = useEvent(() => setIsAddOpen(false));

  const onAdd = useEvent((selected: unknown): void => {
    const key = (selected as IDropdownItem[] | undefined)?.[0]?.value as
      string | undefined;
    handleAddFilter(key);
    setIsAddOpen(false);
  });

  const onApply = useEvent((): void => onFiltersApply?.());

  return (
    <FilterActionsStyled>
      <FilterDropdownWrapper
        isFilterItem={false}
        isOpen={isAddOpen}
        onClose={handleCloseAdd}
        target={
          <FramerTooltip content={t("general.ADD_NEW_FILTER")}>
            <IconButton
              data-e2e="dashboard-add-filter"
              onClick={handleToggleAdd}
            >
              <PIC.PlusSvg />
            </IconButton>
          </FramerTooltip>
        }
      >
        <Select
          isMulti
          // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
          autoFocus
          backspaceRemovesValue={false}
          components={{
            DropdownIndicator: null,
            IndicatorSeparator: null,
            Control: FilterSelectControl,
          }}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          isClearable
          menuIsOpen
          options={addableOptions}
          onChange={onAdd}
          placeholder={`${t("general.SEARCH")}...`}
          styles={stylesComputed}
          tabSelectsValue={false}
          noOptionsMessage={() => t("errors.noOptionsMessage")}
        />
      </FilterDropdownWrapper>
      <FilterActionsDividerStyled />
      <FilterClearButtonReports />
      <FilterActionsDividerStyled />
      <FramerTooltip content={t("general.APPLY_FILTERS")}>
        <IconButton
          data-e2e="dashboard-apply-filters"
          onClick={onApply}
          color="primary"
        >
          <PIC.FilterApplySvg />
        </IconButton>
      </FramerTooltip>
    </FilterActionsStyled>
  );
};

/**
 * The dashboard's report-style filter bar. Offers the union of the cards'
 * reports' filters (see `useDashboardFilterSchema`), less the ones no card here
 * can honour (see `pickOfferableFilters`); each active filter is a
 * reused report multiselect / free-text widget. Live edits go to the reports'
 * shared filter atom (keyed by the dashboard's sentinel report id, 0, since the
 * home route has no `:id`); "Apply" broadcasts them to every card via the
 * applied-filters atom. Scoped to `entity="report"` for correct labels.
 */
export const DashboardFilterBar: FC = () => {
  const { schema, schemaState } = useDashboardFilterSchema();
  const cards = useRecoilValue(dashboardCardsAtom);
  const { reportId } = useReportId();
  const isLayoutResolved = useIsDashboardLayoutResolved();
  const activeFilters = useRecoilValue(reportFiltersWithValuesAF(reportId));
  const offerableSchema = useMemo(
    () => pickOfferableFilters({ cards, schema, schemaState }),
    [cards, schema, schemaState],
  );

  const honourableKeys = useMemo(
    () =>
      isLayoutResolved ? getHonourableKeys({ cards, schemaState }) : undefined,
    [isLayoutResolved, cards, schemaState],
  );

  usePruneUnhonourableFilters({ keys: honourableKeys });

  return (
    <PageContext.Provider value={{ entity: REPORT_FILTER_ENTITY }}>
      {Object.entries(activeFilters ?? {}).map(
        ([templateKey, filterValues]) => {
          const { dropdownSource, selectItemsSource, filterFieldTypeId } =
            schema[templateKey] ?? {};
          if (dropdownSource || selectItemsSource) {
            return (
              <FilterMultiSelectReports
                key={templateKey}
                templateKey={templateKey}
                dropdownSource={dropdownSource}
                selectItemsSource={selectItemsSource}
                initialCheckedValues={filterValues}
              />
            );
          }
          return filterFieldTypeId ? (
            <FiltersByIdsReport
              key={templateKey}
              templateKey={templateKey}
              initialCheckedValues={filterValues}
              filterFieldTypeId={filterFieldTypeId}
            />
          ) : null;
        },
      )}
      <DashboardFilterActions schema={offerableSchema} />
    </PageContext.Provider>
  );
};
